
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import { SimpleTag, TagCondition } from "../types/categoryRules";
import { TagBadge } from "./TagBadge";
import { TagSelector } from "./TagSelector";
import { LogicOperatorSelect } from "./LogicOperatorSelect";

interface ExceptionSectionProps {
  condition: TagCondition;
  tags: SimpleTag[];
  onAddException: () => void;
  onRemoveException: () => void;
  onToggleTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  onUpdateExceptionLogicOperator: (operator: 'any_of' | 'all_of') => void;
}

export const ExceptionSection = ({
  condition,
  tags,
  onAddException,
  onRemoveException,
  onToggleTag,
  onRemoveTag,
  isDropdownOpen,
  onToggleDropdown,
  onUpdateExceptionLogicOperator
}: ExceptionSectionProps) => {
  const getTagWord = (selectedCount: number) => {
    return selectedCount === 1 ? 'tag' : 'tags';
  };

  const exceptionTagCount = condition.exceptionTagIds?.length || 0;

  // Filter out tags that are already used in the main tag selection
  const availableExceptionTags = tags.filter(tag => 
    !condition.selectedTagIds?.includes(tag.id)
  );

  // Show "unless" button only when ruleType is 'only_with' and there are selected tags but no exception yet
  if (condition.ruleType === 'only_with' && !condition.exceptionTagIds?.length && condition.selectedTagIds && condition.selectedTagIds.length > 0) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onAddException}
        className="h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
      >
        <Plus className="w-3 h-3 mr-1" />
        unless
      </Button>
    );
  }

  // Show exception section when it exists
  if (condition.ruleType === 'only_with' && condition.exceptionTagIds?.length) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap">
          <span>Unless the app also contains</span>
          
          <LogicOperatorSelect
            value={condition.exceptionLogicOperator || 'any_of'}
            onValueChange={onUpdateExceptionLogicOperator}
          />
          
          <span>the following {getTagWord(exceptionTagCount)}:</span>
          
          <div className="flex items-center gap-1 flex-wrap">
            {/* Display exception tags inline */}
            {condition.exceptionTagIds && condition.exceptionTagIds.length > 0 && condition.exceptionTagIds.map(tagId => {
              const tag = tags.find(t => t.id === tagId);
              if (!tag) return null;
              return (
                <TagBadge
                  key={tagId}
                  tag={tag}
                  onRemove={() => onRemoveTag(tagId)}
                />
              );
            })}

            {/* Tag selector for exception tags - only show available exception tags */}
            <TagSelector
              tags={availableExceptionTags}
              selectedTagIds={condition.exceptionTagIds || []}
              onToggleTag={onToggleTag}
              isOpen={isDropdownOpen}
              onToggle={onToggleDropdown}
            />

            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveException}
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
